// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/taches.js — LA PORTE « MES TÂCHES ».
//
// ══ POURQUOI CETTE FAMILLE EXISTE (2026-09-16) ══════════════════════════════
//
// L'inventaire (docs/handoff/vague0-operateur/COUVERTURE_GESTES_INVENTAIRE.md
// § 5.3) a mesuré `admin_tasks` : 7 `update`, 2 `insert`, 1 `delete` partent
// DIRECTEMENT du navigateur, et aucune action serveur ne les nomme. Sa phrase,
// telle quelle : « c'est le trou que Dimitri sentira le plus vite, parce qu'il
// couvre Mes tâches — l'écran le plus utilisé au quotidien ».
//
// Un seul levier y existait, `admin_task_done` (cocher). Créer, replanifier,
// renommer, changer la priorité ou le logement : rien. L'opérateur répondait
// « ouvre Mes tâches » — c'est-à-dire l'écran à ouvrir que Dimitri ne veut plus.
//
// ══ LE SUJET EST « terrain », ET C'EST LA MAQUETTE QUI LE DIT ═══════════════
//
// `_forme.js` glose `terrain` ainsi : « les ménages, le contrôle, le stock, les
// incidents, LES TÂCHES ». La question de Dimitri — « tu sais faire quoi sur
// mes tâches ? » — tombe donc là, et `admin_task_done` y est déjà rangé : deux
// gestes du même écran sous deux sujets différents rendraient la réponse
// incomplète sans que personne ne le voie.
//
// ⚠️ ET PAS « operateur ». Ce sujet-là est la famille FERMÉE de sa propre
// mécanique (E5) : les tâches sont celles de DIMITRI, pas les siennes.
//
// ══ LA VAGUE 5 — « le terrain, le quotidien qui ne sort pas » ══════════════
//
// Ces gestes ne coûtent rien, ne partent chez personne, et se constatent sur
// l'écran le plus regardé de la journée. Ils n'entrent pourtant dans AUCUNE
// vague déjà vérifiée : la règle du dépôt est qu'on n'ajoute jamais un levier
// à une vague dont Dimitri a déjà relu le contenu. D'où la vague 5, fermée par
// défaut comme les autres — elle s'ouvre levier par levier
// (`OPERATEUR_GESTES_VAGUE="5:tache_champ_set"`).

// ══ « vise=logement » AU CATALOGUE — LE NOM DIT, PAS L'IDENTIFIANT ═════════
//
// Les leviers de cette famille qui portent `args_resolus` ne montrent plus
// `logement_id` au modèle : il pose « Cocon Naturel » dans `vise`, et le
// serveur retrouve `lc1` au moment de composer la carte
// (`lib/reperes-geste.js`). L'argument RESTE dans `args_autorises` — l'en
// retirer ferait refuser la carte au clic (`filtrerArgs`, rejoué par le
// navigateur) et casserait les aperçus qui le citent en `depend_de`.
//
// ⚠️ ET SI LE MODÈLE LE COMPOSE QUAND MÊME, LA CARTE NE VIENT PAS. On ne lui
// demande pas de s'abstenir : on rend la chose sans effet, et l'écran demande
// à Dimitri de nommer le logement. « Cocon » tout seul désigne DEUX logements
// aux commissions différentes — ce n'est pas un détail de confort.
const LEVIERS_TACHES = Object.freeze({
  // ══ MODIFIER UNE TÂCHE — UNE ACTION GÉNÉRIQUE, UN CATALOGUE FERMÉ ═══════
  //
  // Le patron est celui de `logement_champ_set`, et c'est la recommandation
  // du cadrage mot pour mot : « une seule action serveur générique par TABLE
  // […] pas une action par bouton ». Cinq champs, une lecture avant l'écriture,
  // un jeton de version, un diff intégral.
  //
  // ⚠️ UN SEUL LEVIER, PARCE QU'AUCUN MARQUAGE NE SÉPARE CES CINQ CHAMPS. Sur
  // la fiche d'un logement il en fallait deux (la commission est de l'argent,
  // un code de porte non). Ici rien ne coûte, rien ne sort : les cinq champs
  // portent exactement la même promesse, et deux leviers auraient obligé à
  // déclarer un verbe pour les distinguer sans rien protéger de plus.
  //
  // ⚠️ `nouvelle` EST LIBRE PAR CONSTRUCTION : sa forme dépend du champ (un
  // titre, une date AAAA-MM-JJ, `ouvert`/`fait`, une priorité, un identifiant
  // de logement — ou le vide, qui EFFACE l'échéance ou détache le logement).
  // Le serveur la valide contre le champ visé et refuse tout le reste ; la
  // carte la relit AVANT le clic (l'aperçu rend les valeurs fermées de
  // `statut` et `priorite`, et grise le bouton si la valeur n'y entre pas).
  tache_champ_set: {
    ecran: "mes-taches",
    endpoint: "/api/beds24-mapping",
    action: "tache_champ_set",
    args_autorises: ["id", "champ", "nouvelle"],
    // ⚠️ « Décale la tâche du site internet à lundi » — le `dit_comme` de ce
    // levier commence par le mot d'objet, et sans son retrait il ne résolvait
    // RIEN. Le repère est le titre ; l'identifiant n'entre ni ne sort du
    // prompt.
    args_resolus: {
      id: { objet: "tache", par: ["nom"] },
    },
    args_valeurs: {
      champ: ["titre", "echeance", "statut", "priorite", "logement"],
    },
    args_libres: ["nouvelle"],
    apercu: {
      lecture: "tache_lire",
      depend_de: ["id", "champ"],
      jeton: "x-apercu-version",
    },
    titre: "Modifier une tâche de Mes tâches",
    constatable:
      "la tâche change sous vos yeux dans Mes tâches : le titre réécrit, la date de la semaine où elle apparaît, la pastille d’urgence, le logement affiché — et « faite » la range avec les tâches terminées, datée du jour. La carte montre la valeur d’avant et celle d’après avant que vous cliquiez.",
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour:
      "un titre réécrit ne se récupère pas : l’ancien n’est gardé nulle part. Le reste se repose (une date, une urgence, « faite » se remet en « à faire »).",
    ne_fait_pas:
      "il ne supprime aucune tâche, n’en archive aucune, ne touche ni à la précision écrite dessous ni aux étiquettes, et ne prévient personne : c’est votre tableau, il ne sort pas de l’application.",
    dit_comme: [
      "Décale la tâche EDF à lundi",
      "C’est fait pour la facture de Pierre",
      "Cette tâche concerne le Cocon, range-la dessus",
      "Mets cette tâche en urgent",
      "Enlève la date de cette tâche, je la ferai plus tard",
    ],
    marquages: [],
    vague: 5,
    motif_requis: false,
    autonome: false,
    // ⚠️ `autonome: false` ALORS QUE LES QUATRE QUESTIONS DIRAIENT OUI. Un
    // titre réécrit n'a PAS de chemin de retour (question 1) : l'ancien
    // n'existe nulle part, et c'est ce que dit `sans_retour`. Le doute tranche
    // vers false, comme la règle l'exige.
    //
    // E1 ✔ (un champ scalaire, la carte montre l'ancienne valeur ; aucune
    // suppression, aucun fichier) · E2 ✔ (Mes tâches, tout de suite) ·
    // E3 ✔ (les MÊMES couples de colonnes que l'écran : statut+date de
    // réalisation, échéance+jour entier, logement+propriétaire déduit) ·
    // E4 ✔ (rien à attester : Dimitri constate lui-même que c'est fait) ·
    // E5 ✔ (ce sont SES tâches, pas la mécanique de l'opérateur).
    //
    // ⚠️ POURQUOI PAS D'ÉTIQUETTE ICI. Poser une étiquette demande de composer
    // l'identifiant technique d'une ligne de `task_labels`, que ni le modèle ni
    // Dimitri ne connaissent — la garde anti-choix l'interdit, et un
    // identifiant inventé donnerait un bouton refusé sous le clic. La voie
    // propre est `args_choisis` avec une lecture qui rend la liste des
    // étiquettes ; elle n'est pas montée, et le serveur REFUSE ce champ.
  },

  // ══ CRÉER UNE TÂCHE — VAGUE 10, PARCE QU'ELLE FAIT EXISTER ══════════════
  //
  // ⚠️ ELLE N'EST PAS DANS LA VAGUE 5 AVEC SA VOISINE, ET C'EST LA RÈGLE QUI
  // L'EXIGE. La vague 10 est définie ainsi : « les vagues 1 à 9 règlent,
  // publient, répondent ; celle-ci fait EXISTER quelque chose ». Une tâche
  // neuve en est une. Ranger une création dans une vague de réglages ferait
  // mentir la vague, et c'est précisément pour ça que Dimitri a demandé une
  // vague séparée le 2026-09-15.
  //
  // ⚠️ L'APERÇU MONTRE LA JOURNÉE, PAS LA TÂCHE. Il n'y a pas d'« avant » à
  // afficher sur une tâche qui n'existe pas encore — mais il y a une décision
  // à éclairer : ce qui est DÉJÀ prévu ce jour-là. C'est le même choix que
  // `proprietaire_creer`, dont l'aperçu montre le doublon d'adresse.
  tache_creer: {
    ecran: "mes-taches",
    endpoint: "/api/beds24-mapping",
    action: "tache_creer",
    args_autorises: ["titre", "echeance", "logement_id"],
    args_resolus: {
      logement_id: { objet: "logement", par: ["logement"], requis: [] },
    },
    // Le titre est DICTÉ par Dimitri (« appeler le plombier ») : aucune liste
    // ne peut l'énumérer. Le serveur le borne (une ligne, 120 caractères) et
    // refuse tout le reste. L'échéance est une date, le logement un
    // identifiant du parc — refusé s'il n'y est pas.
    args_libres: ["titre", "echeance", "logement_id"],
    apercu: {
      lecture: "taches_lire",
      depend_de: ["echeance"],
      jeton: "x-apercu-version",
    },
    titre: "Ajouter une tâche dans Mes tâches",
    constatable:
      "la tâche apparaît dans Mes tâches, à faire, au jour que vous avez dit (sans date, elle se range avec celles qui n’en ont pas) — et sur le logement concerné si vous l’avez nommé. La carte vous montre d’abord ce qui est déjà prévu ce jour-là.",
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour: null, // l'écran sait la supprimer, et personne n'est prévenu
    ne_fait_pas:
      "elle ne prévient personne et ne pose aucun rappel : rien ne sonnera le jour dit, la tâche vous attend dans l’écran. Elle n’ajoute ni précision écrite ni étiquette — vous les ajoutez ensuite si vous voulez.",
    dit_comme: [
      "Rappelle-moi de changer les draps du Cocon vendredi",
      "Ajoute une tâche : appeler le plombier",
      "Note-moi de relancer Pierre pour sa facture lundi",
      "Mets-moi une tâche pour vérifier la chaudière",
    ],
    marquages: [],
    vague: 10,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (une création seule — rien n'est effacé, rien ne part) · E2 ✔ (elle
    // est dans la liste juste après) · E3 ✔ (mêmes colonnes et même position de
    // fin de tableau que le bouton « Nouvelle tâche ») · E4 ✔ (le titre est
    // dicté par Dimitri, rien n'est attesté à sa place) · E5 ✔.
  },
});

module.exports = { LEVIERS_TACHES };
